// CLI command handlers (ARCHITECTURE.md §14.1).
//
// Pure business logic for each subcommand. Commander wiring lives in index.ts.

import { rm } from "node:fs/promises";

import { analyzeImpact, buildCallGraph } from "../impact/index.js";
import type { CallGraph, CallGraphNode } from "../impact/index.js";
import type { ImpactDirection, ImpactReport } from "../impact/types.js";
import type { EdgeClassification } from "../types.js";
import type { EvidenceType } from "../evidence/types.js";
import { resolveDiffSeeds } from "./git-diff.js";
import { generateContextPack } from "../context/index.js";
import type { ContextGranularity } from "../context/index.js";
import type { ContextPack } from "../context/types.js";
import { detectGaps, runSemanticAnalysis } from "../semantic/index.js";
import type { SemanticAnalysisResult } from "../semantic/index.js";
import { createLlmAdapter, ADAPTER_NAMES } from "../semantic/adapters/index.js";
import type { GapCandidate } from "../semantic/types.js";
import type { DependencyGraph } from "../dependency/types.js";
import { openStore, persistCandidateEdges } from "../store/index.js";
import { getAllFiles } from "../store/reader.js";
import {
  serializeDependencyGraph,
  writeOutput,
  type OutputFormat,
  type Writer,
} from "./output.js";
import { AnalysisError, buildProjectGraph } from "./project.js";

// ---------------------------------------------------------------------------
// Common options & writer
// ---------------------------------------------------------------------------

export interface CommonOptions {
  projectRoot: string;
  dsl?: string;
  config?: string;
  format: OutputFormat;
  /** Edge classifications to traverse (Issue #21). */
  edgeTypes: EdgeClassification[];
  /** Evidence types to exclude from relevance scoring (Issue #21). */
  evidenceExclude: EvidenceType[];
  /** Cap the number of results (impact / context-pack). */
  limit?: number;
  /** Include relevance scores in human-readable output. */
  showRelevance: boolean;
  cache: boolean;
  quiet: boolean;
}

/** Resolve the active output writer; `undefined` falls back to stdout. */
let activeWriter: Writer | undefined;

/** Override the output sink (used by tests). Pass `undefined` to restore stdout. */
export function setWriter(writer: Writer | undefined): void {
  activeWriter = writer;
}

function writerOrDefault(writer: Writer | undefined): Writer | undefined {
  return writer ?? activeWriter;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Files belonging to `artifactId`, derived from the graph's artifact mapping. */
export function filesForArtifact(
  graph: DependencyGraph,
  artifactId: string,
): string[] {
  const files: string[] = [];
  for (const [path, ids] of graph.artifactMapping) {
    if (ids.includes(artifactId)) files.push(path);
  }
  return files;
}

export function resolveChangedFiles(
  graph: DependencyGraph,
  opts: { files?: string[]; artifact?: string; symbols?: string[] },
): string[] {
  const files = opts.files ?? [];
  const symbols = opts.symbols ?? [];
  const artifact = opts.artifact;
  if (files.length === 0 && symbols.length === 0 && !artifact) {
    throw new AnalysisError(
      "Provide --files <paths...>, --symbols <ids...>, or --artifact <id>.",
    );
  }
  // Symbol ids (filePath#name) are valid seeds alongside file paths (§5.1).
  const changed = [...files, ...symbols];
  if (artifact) changed.push(...filesForArtifact(graph, artifact));
  if (changed.length === 0) {
    throw new AnalysisError(`No files found for artifact '${artifact}'.`);
  }
  return [...new Set(changed)];
}

export function renderGraphText(graph: DependencyGraph): string {
  const fileNodes = [...graph.nodes.values()].filter((n) => n.kind === "file");
  const artifactNodes = [...graph.nodes.values()].filter(
    (n) => n.kind === "artifact",
  );
  const lines = [
    "Dependency Graph",
    `  nodes:     ${graph.nodes.size}`,
    `  files:     ${fileNodes.length}`,
    `  artifacts: ${artifactNodes.length}`,
    `  edges:     ${graph.edges.length}`,
  ];
  if (graph.artifactMapping.size > 0) {
    lines.push("  artifact mapping:");
    const byArtifact = new Map<string, number>();
    for (const ids of graph.artifactMapping.values()) {
      for (const id of ids) byArtifact.set(id, (byArtifact.get(id) ?? 0) + 1);
    }
    for (const [id, count] of [...byArtifact].sort()) {
      lines.push(`    ${id}: ${count} file(s)`);
    }
  }
  return lines.join("\n");
}

export function renderImpactText(
  report: ImpactReport,
  showRelevance = false,
): string {
  const lines = [
    "Impact Report",
    `  direction:           ${report.direction}`,
    `  changed:             ${report.changed.length}`,
    `  directly affected:   ${report.directlyAffected.length}`,
    `  transitively:        ${report.transitivelyAffected.length}`,
    `  affected artifacts:  ${report.affectedArtifacts.length}`,
    `  affected validations:${report.affectedValidations.length}`,
    `  affected agents:     ${report.affectedAgents.length}`,
    `  affected workflows:  ${report.affectedWorkflows.length}`,
    `  test associations:   ${report.testMapping.length}`,
    `  max confidence:      ${report.maxConfidence}`,
  ];
  if (report.directlyAffected.length > 0) {
    lines.push("  directly affected:");
    for (const e of report.directlyAffected)
      lines.push(`    ${formatEntry(e, showRelevance)}`);
  }
  const affectedSymbols = [
    ...report.directlyAffected,
    ...report.transitivelyAffected,
  ].filter((e) => e.symbolId);
  if (affectedSymbols.length > 0) {
    lines.push("  affected symbols:");
    for (const e of affectedSymbols)
      lines.push(`    ${formatEntry(e, showRelevance)}`);
  }
  return lines.join("\n");
}

/** Render an AffectedEntry: symbol id + line range when present, else path. */
function formatEntry(
  e: ImpactReport["directlyAffected"][number],
  showRelevance = false,
): string {
  const rel =
    showRelevance && e.relevanceScore !== undefined
      ? ` relevance=${e.relevanceScore.toFixed(2)}`
      : "";
  if (e.symbolId) {
    const range =
      e.line !== undefined && e.endLine !== undefined
        ? ` (lines ${e.line}-${e.endLine})`
        : "";
    return `${e.symbolId}${range}${rel}`;
  }
  return `${e.path}${rel}`;
}

/** Render a call graph as an indented tree (proposal §7.3). */
export function renderCallGraphText(callGraph: CallGraph): string {
  const lines = [callGraph.root.symbolId];

  const walk = (node: CallGraphNode, prefix: string): void => {
    node.children.forEach((child, index) => {
      const last = index === node.children.length - 1;
      const branch = last ? "└── " : "├── ";
      const lineInfo = child.line !== undefined ? ` (calls, line ${child.line})` : "";
      const cycle = child.truncated ? " (cycle)" : "";
      lines.push(`${prefix}${branch}${child.symbolId}${lineInfo}${cycle}`);
      walk(child, `${prefix}${last ? "    " : "│   "}`);
    });
  };

  walk(callGraph.root, "");
  return lines.join("\n");
}

/** Quote and escape a string for use as a Graphviz node/label identifier. */
function dotQuote(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/** Render a call graph as a Graphviz digraph (proposal §7.3). */
export function renderCallGraphDot(callGraph: CallGraph): string {
  const lines = [
    "digraph callgraph {",
    "  rankdir=LR;",
    "  node [shape=box, fontname=\"monospace\"];",
  ];

  const declared = new Set<string>();
  const emittedEdges = new Set<string>();

  const declare = (node: CallGraphNode): void => {
    if (declared.has(node.symbolId)) return;
    declared.add(node.symbolId);
    lines.push(`  ${dotQuote(node.symbolId)};`);
  };

  const walk = (node: CallGraphNode): void => {
    declare(node);
    for (const child of node.children) {
      declare(child);
      const label = child.line !== undefined ? ` [label="line ${child.line}"]` : "";
      const edge = `  ${dotQuote(node.symbolId)} -> ${dotQuote(child.symbolId)}${label};`;
      const key = `${node.symbolId} ${child.symbolId}`;
      if (!emittedEdges.has(key)) {
        emittedEdges.add(key);
        lines.push(edge);
      }
      walk(child);
    }
  };

  walk(callGraph.root);
  lines.push("}");
  return lines.join("\n");
}

export function renderContextPackText(pack: ContextPack): string {
  const lines = [
    "Context Pack",
    pack.taskId ? `  task:            ${pack.taskId}` : "  task:            (none)",
    `  total tokens:    ${pack.metadata.totalTokens}`,
    `  budget tokens:   ${pack.metadata.budgetTokens}`,
    `  files included:  ${pack.metadata.filesIncluded}`,
    `  files referenced:${pack.metadata.filesReferenced}`,
    `  files excluded:  ${pack.metadata.filesExcluded}`,
    `  artifacts:       ${pack.metadata.artifactsCovered.length}`,
    "  entries:",
  ];
  for (const entry of pack.entries) {
    lines.push(
      `    ${entry.path} [${entry.contentMode}] score=${entry.relevanceScore.toFixed(
        2,
      )}`,
    );
    // Symbol-scoped entries (§6.1): list each included symbol and its range.
    if (entry.contentMode === "symbol-section" && entry.sections) {
      for (const s of entry.sections) {
        const name = s.symbolName ?? s.symbolId ?? "symbol";
        lines.push(`      ${name} (lines ${s.startLine}-${s.endLine})`);
      }
    }
  }
  return lines.join("\n");
}

export function listDependencies(
  graph: DependencyGraph,
  file: string,
  direction: "forward" | "reverse",
): string[] {
  const deps = new Set<string>();
  for (const edge of graph.edges) {
    if (direction === "forward" && edge.from === file) deps.add(edge.to);
    if (direction === "reverse" && edge.to === file) deps.add(edge.from);
  }
  return [...deps].sort();
}

export function renderDependenciesText(
  file: string,
  direction: "forward" | "reverse",
  deps: string[],
): string {
  const label = direction === "forward" ? "depends on" : "depended on by";
  const lines = [`${file} ${label} (${deps.length}):`];
  for (const dep of deps) lines.push(`  ${dep}`);
  return lines.join("\n");
}

export function renderGapsText(gaps: GapCandidate[]): string {
  const lines = ["Semantic Gaps", `  gaps: ${gaps.length}`];
  const byReason = new Map<string, number>();
  for (const gap of gaps) {
    byReason.set(gap.gapReason, (byReason.get(gap.gapReason) ?? 0) + 1);
  }
  for (const [reason, count] of [...byReason].sort()) {
    lines.push(`    ${reason}: ${count}`);
  }
  for (const gap of gaps) {
    lines.push(`  ${gap.fileA} <-> ${gap.fileB} [${gap.gapReason}]`);
  }
  return lines.join("\n");
}

export function renderSemanticResultText(result: SemanticAnalysisResult): string {
  const lines = [
    "Semantic Analysis",
    `  gaps detected:     ${result.gaps.length}`,
    `  candidate edges:   ${result.edges.length}`,
  ];
  if (result.gaps.length > 0) {
    lines.push("  gaps:");
    for (const gap of result.gaps) {
      lines.push(`    ${gap.fileA} <-> ${gap.fileB} [${gap.gapReason}]`);
    }
  }
  if (result.edges.length > 0) {
    lines.push("  candidate edges:");
    for (const edge of result.edges) {
      const confidence = edge.evidence[0]?.confidence;
      const conf =
        confidence !== undefined ? ` confidence=${confidence}` : "";
      lines.push(`    ${edge.from} -> ${edge.to} [${edge.kind}]${conf}`);
    }
  }
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

export async function handleGraph(
  common: CommonOptions,
  writer?: Writer,
): Promise<void> {
  const { graph, store } = await buildProjectGraph({
    projectRoot: common.projectRoot,
    dslPath: common.dsl,
    useCache: common.cache,
  });
  try {
    writeOutput({
      format: common.format,
      data: serializeDependencyGraph(graph),
      text: () => renderGraphText(graph),
      writer: writerOrDefault(writer),
    });
  } finally {
    store?.close();
  }
}

export async function handleImpact(
  common: CommonOptions,
  opts: {
    files?: string[];
    artifact?: string;
    symbols?: string[];
    diff?: string;
    direction: ImpactDirection;
    maxDepth?: number;
  },
  writer?: Writer,
): Promise<void> {
  const { graph, navigationIndex, store } = await buildProjectGraph({
    projectRoot: common.projectRoot,
    dslPath: common.dsl,
    useCache: common.cache,
  });

  try {
    // `--diff <ref>` derives symbol-level seeds from a git diff; otherwise the
    // seeds come from explicit --files / --symbols / --artifact.
    const changed = opts.diff
      ? resolveDiffSeeds(graph, opts.diff, common.projectRoot)
      : resolveChangedFiles(graph, opts);
    if (changed.length === 0) {
      throw new AnalysisError(
        `No changes found in git diff against '${opts.diff}'.`,
      );
    }
    const report = await analyzeImpact({
      graph,
      changed,
      direction: opts.direction,
      maxDepth: opts.maxDepth,
      edgeTypes: common.edgeTypes,
      evidenceExclude: common.evidenceExclude,
      ...(common.limit !== undefined ? { limit: common.limit } : {}),
      navigationIndex,
    });
    writeOutput({
      format: common.format,
      data: report,
      text: () => renderImpactText(report, common.showRelevance),
      writer: writerOrDefault(writer),
    });
  } finally {
    store?.close();
  }
}

export async function handleCallgraph(
  common: CommonOptions,
  opts: {
    entry: string;
    depth: number;
    format: "json" | "text" | "dot";
    direction: "forward" | "reverse";
  },
  writer?: Writer,
): Promise<void> {
  const { graph, store } = await buildProjectGraph({
    projectRoot: common.projectRoot,
    dslPath: common.dsl,
    useCache: common.cache,
  });

  try {
    if (!graph.ast.nodes.has(opts.entry)) {
      throw new AnalysisError(
        `Entry symbol '${opts.entry}' not found in the graph. Use a symbol id like 'src/server.ts#handleRequest'.`,
      );
    }
    const callGraph = buildCallGraph({
      graph,
      entry: opts.entry,
      maxDepth: opts.depth,
      direction: opts.direction,
    });

    const write =
      writerOrDefault(writer) ?? ((line) => process.stdout.write(`${line}\n`));
    switch (opts.format) {
      case "json":
        write(JSON.stringify(callGraph, null, 2));
        break;
      case "dot":
        write(renderCallGraphDot(callGraph));
        break;
      case "text":
        write(renderCallGraphText(callGraph));
        break;
    }
  } finally {
    store?.close();
  }
}

export async function handleContextPack(
  common: CommonOptions,
  opts: {
    files: string[];
    task?: string;
    budget: number;
    granularity?: ContextGranularity;
  },
  writer?: Writer,
): Promise<void> {
  if (opts.files.length === 0) {
    throw new AnalysisError("Provide --files <paths...>.");
  }

  const t0 = performance.now();
  const { graph, navigationIndex, store, fileContents } =
    await buildProjectGraph({
      projectRoot: common.projectRoot,
      dslPath: common.dsl,
      useCache: common.cache,
    });
  const t1 = performance.now();

  try {
    const pack = await generateContextPack({
      graph,
      changed: opts.files,
      budgetTokens: opts.budget,
      edgeTypes: common.edgeTypes,
      evidenceExclude: common.evidenceExclude,
      ...(common.limit !== undefined ? { limit: common.limit } : {}),
      taskId: opts.task,
      projectRoot: common.projectRoot,
      fileContents,
      ...(opts.granularity ? { granularity: opts.granularity } : {}),
      ...(navigationIndex ? { navigationIndex } : {}),
    });
    const t2 = performance.now();
    console.error(`[perf] buildProjectGraph: ${(t1 - t0).toFixed(0)}ms`);
    console.error(`[perf] generateContextPack: ${(t2 - t1).toFixed(0)}ms`);
    console.error(`[perf] handleContextPack total: ${(t2 - t0).toFixed(0)}ms`);
    writeOutput({
      format: common.format,
      data: pack,
      text: () => renderContextPackText(pack),
      writer: writerOrDefault(writer),
    });
  } finally {
    store?.close();
  }
}

export async function handleDependencies(
  common: CommonOptions,
  opts: { file: string; direction: "forward" | "reverse" },
  writer?: Writer,
): Promise<void> {
  const { graph, store } = await buildProjectGraph({
    projectRoot: common.projectRoot,
    dslPath: common.dsl,
    useCache: common.cache,
  });

  try {
    const deps = listDependencies(graph, opts.file, opts.direction);
    writeOutput({
      format: common.format,
      data: { file: opts.file, direction: opts.direction, dependencies: deps },
      text: () => renderDependenciesText(opts.file, opts.direction, deps),
      writer: writerOrDefault(writer),
    });
  } finally {
    store?.close();
  }
}

export async function handleSemantic(
  common: CommonOptions,
  opts: {
    dryRun: boolean;
    adapter?: string;
    model?: string;
    maxCandidates?: number;
  },
  writer?: Writer,
): Promise<void> {
  const { graph, navigationIndex, store } = await buildProjectGraph({
    projectRoot: common.projectRoot,
    dslPath: common.dsl,
    useCache: common.cache,
  });

  try {
    const gaps = detectGaps({
      astGraph: graph.ast,
      artifactMapping: graph.artifactMapping,
      navigationIndex,
    });

    if (opts.dryRun) {
      writeOutput({
        format: common.format,
        data: { gaps },
        text: () => renderGapsText(gaps),
        writer: writerOrDefault(writer),
      });
      return;
    }

    if (!opts.adapter) {
      throw new AnalysisError(
        `No LLM adapter specified. Use --adapter <name> (${ADAPTER_NAMES.join(", ")}) or --dry-run to inspect detected gaps.`,
      );
    }

    let llmAdapter;
    try {
      llmAdapter = await createLlmAdapter(opts.adapter, { model: opts.model });
    } catch (error) {
      throw new AnalysisError((error as Error).message);
    }

    const result = await runSemanticAnalysis({
      astGraph: graph.ast,
      artifactMapping: graph.artifactMapping,
      projectRoot: common.projectRoot,
      llmAdapter,
      maxCandidatesPerRun: opts.maxCandidates,
      navigationIndex,
    });

    if (store && result.edges.length > 0) {
      persistCandidateEdges(store, result.edges);
    }

    writeOutput({
      format: common.format,
      data: result,
      text: () => renderSemanticResultText(result),
      writer: writerOrDefault(writer),
    });
  } finally {
    store?.close();
  }
}

export async function handleCacheStatus(
  common: CommonOptions,
  writer?: Writer,
): Promise<void> {
  const store = await openStore();
  try {
    const files = getAllFiles(store);
    const edgeCount = 0;
    const lastRebuild = store.backend.getMetadata("last_rebuild") ?? null;
    const data = {
      backend: store.kind,
      path: store.dbPath,
      schemaVersion: store.backend.getSchemaVersion(),
      fileCount: files.length,
      edgeCount,
      lastRebuild,
    };
    writeOutput({
      format: common.format,
      data,
      text: () =>
        [
          "Cache Status",
          `  backend:        ${data.backend}`,
          `  path:           ${data.path ?? "(in-memory)"}`,
          `  schema version: ${data.schemaVersion}`,
          `  files:          ${data.fileCount}`,
          `  edges:          ${data.edgeCount}`,
          `  last rebuild:   ${data.lastRebuild ?? "(never)"}`,
        ].join("\n"),
      writer: writerOrDefault(writer),
    });
  } finally {
    store.close();
  }
}

export async function handleCacheClear(
  common: CommonOptions,
  writer?: Writer,
): Promise<void> {
  const store = await openStore();
  const path = store.dbPath;
  store.close();

  const write =
    writerOrDefault(writer) ?? ((line) => process.stdout.write(`${line}\n`));
  if (!path) {
    write("No persistent store to clear (in-memory backend).");
    return;
  }
  await rm(path, { force: true });
  await rm(`${path}-wal`, { force: true });
  await rm(`${path}-shm`, { force: true });
  if (!common.quiet) write(`Cleared store at ${path}`);
}
